<template>
  <div>
    <v-row>
      <v-col cols="12" md="2">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCard
          v-else
          identifier="totalItemsCard"
          :label="$t('totalItems')"
          :count="totalItems"       
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCard
          v-else
          identifier="totalSubItemsCard"
          :label="$t('totalSubItems')"
          :tooltip="$t('totalSubItemsTooltip')" 
          :count="totalSubItems"   
        />  
      </v-col>
      <v-col cols="12" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalControlledByMyTeams"
          :label="$t('controlledByYourTeams')"
          :count="totalControlledByYourTeams"
          @filter-change="handleFilterChange"
          v-model:selected="filterByControlledByMyTeams"   
        />  
      </v-col>
      <v-col cols="12" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalCreatedByYourTeams"
          :label="$t('createdByYourTeams')"
          :count="totalCreatedByYourTeams"
          @filter-change="handleFilterChange"
          v-model:selected="filterByCreatedByMyTeams"   
        /> 
      </v-col>
      <v-col cols="12" md="2">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalCreatedByYou"
          :label="$t('createdByYou')"
          :count="totalCreatedByYou"
          @filter-change="handleFilterChange"
          v-model:selected="filterByCreatedByYou"   
        /> 
      </v-col>
    </v-row>

    <v-row>        
      <v-col cols="12" md="8">
        <ControllerCategoryStackedBarChart
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
      <v-col cols="12" md="4">
        <ControllerDonutChart
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>

    <!-- Items Data Table -->
    <v-row>
      <v-col cols="12" md="12">
        <ItemsGrid
          @chartClick="handleChartClick"
          :filterContext="filterContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import ControllerDonutChart from './charts/ControllerDonutChart.vue';
import ControllerCategoryStackedBarChart from './charts/ControllerCategoryStackedBarChart.vue';
import ItemsGrid from './grids/ItemsGrid.vue';
import TotalCard from './cards/TotalCard.vue';
import TotalCardWithSwitch from './cards/TotalCardWithSwitch.vue';

const store = useStore();
const settings = computed(() => store.state.settings);
const dataLoaded = computed(() => store.getters.getDataLoaded);

const filterContext = computed(() => store.state.filterItemsContext);
const filteredItems = computed(() => store.getters.getFilteredItems);
const userTeamsObj = computed(() => store.getters.getUserTeamsObj);
const userId = computed(() => store.getters.getUserId);
const conn = store.getters.getDBConnection;

const totalItems = ref(0);  
const totalSubItems = ref(0);
const totalControlledByYourTeams = ref(0);
const totalCreatedByYourTeams = ref(0);
const totalCreatedByYou = ref(0);

const filterByControlledByMyTeams = ref(false);
const filterByCreatedByMyTeams = ref(false);
const filterByCreatedByYou = ref(false);

const computeCardValues = async () => {
  if (!dataLoaded.value || !isMounted.value) return;
  
  // Build a combined query to get all counts at once
  const combinedQuery = `
    SELECT 
        COUNT(DISTINCT id) AS TotalItems,
        Count(*) AS TotalSubItems,
        COUNT(CASE WHEN controlledByTeamId IN (
            SELECT Id 
            FROM team_users
            WHERE list_contains(list_transform(Users, u -> u.Id), '${userId.value}')
            AND list_contains(list_transform(Users, u -> u."Current"), TRUE)
        ) THEN id END) AS ControlledByYourTeams,
        COUNT(CASE WHEN createdByTeamId IN (
            SELECT Id
            FROM team_users
            WHERE list_contains(list_transform(Users, u -> u.Id), '${userId.value}')
            AND list_contains(list_transform(Users, u -> u."Current"), TRUE)
        ) THEN id END) AS CreatedByYourTeams,
        COUNT(CASE WHEN createdByUserId = '${userId.value}' THEN id END) AS CreatedByYou
    FROM filteredItems
  `;

  try {
    const result = await conn.query(combinedQuery);
    const row = result.toArray()[0];
    
    if (!row) {
      totalItems.value = 0;
      totalSubItems.value = 0;
      totalControlledByYourTeams.value = 0;
      totalCreatedByYourTeams.value = 0;
      totalCreatedByYou.value = 0;
      return;
    }

    const jsonRow = row.toJSON();
    
    totalItems.value = Number(jsonRow.TotalItems);
    totalSubItems.value = Number(jsonRow.TotalSubItems);
    totalControlledByYourTeams.value = Number(jsonRow.ControlledByYourTeams);
    totalCreatedByYourTeams.value = Number(jsonRow.CreatedByYourTeams);
    totalCreatedByYou.value = Number(jsonRow.CreatedByYou);
    
  } catch (error) {
    console.error("Error executing combined query:", error);
    totalItems.value = 0;
    totalSubItems.value = 0;
    totalControlledByYourTeams.value = 0;
    totalCreatedByYourTeams.value = 0;
    totalCreatedByYou.value = 0;
  }
};

const handleFilterChange = ({ id, selected }) => {
  if (id === 'totalControlledByMyTeams') {
    filterByControlledByYourTeams(selected);
  } else if (id === 'totalCreatedByYourTeams') {
    filterByCreatedByYourTeams(selected);
  } else if (id === 'totalCreatedByYou') {
    filterByCreatedByMe(selected);
  }
};

const filterByControlledByYourTeams = async (selected) => {
  if (selected) {
    try {
      const query = `
        SELECT si.id
        FROM items i
        JOIN subitems si ON i.id = si.item_id
        WHERE i.controlledByTeamId IN (
          SELECT team_id
          FROM user_teams
          WHERE user_id = '${userId.value}'
        )
      `;
      
      const result = await conn.query(query);
      const rows = result.toArray();
      
      const controlledByYourTeamsFilter = {};
      rows.forEach(row => {
        controlledByYourTeamsFilter[row.id] = true;
      });
      
      store.commit('updateFilter', {
        chartId: 'controlledByYourTeamCardFilterContext', 
        ids: controlledByYourTeamsFilter
      });
    } catch (error) {
      console.error("Error filtering by controlled by your teams:", error);
    }
  } else {
    store.commit('updateFilter', {
      chartId: 'controlledByYourTeamCardFilterContext', 
      ids: {}
    });
  }
};

const filterByCreatedByYourTeams = async (selected) => {
  if (selected) {
    try {
      const query = `
        SELECT si.id
        FROM items i
        JOIN subitems si ON i.id = si.item_id
        WHERE i.createdByTeamId IN (
          SELECT team_id
          FROM user_teams
          WHERE user_id = '${userId.value}'
        )
      `;
      
      const result = await conn.query(query);
      const rows = result.toArray();
      
      const createdByYourTeamsFilter = {};
      rows.forEach(row => {
        createdByYourTeamsFilter[row.id] = true;
      });
      
      store.commit('updateFilter', {
        chartId: 'createdByYourTeamCardFilterContext', 
        ids: createdByYourTeamsFilter
      });
    } catch (error) {
      console.error("Error filtering by created by your teams:", error);
    }
  } else {
    store.commit('updateFilter', {
      chartId: 'createdByYourTeamCardFilterContext', 
      ids: {}
    });
  }
};

const filterByCreatedByMe = async (selected) => {
  if (selected) {
    try {
      const query = `
        SELECT si.id
        FROM items i
        JOIN subitems si ON i.id = si.item_id
        WHERE i.createdByUserId = '${userId.value}'
      `;
      
      const result = await conn.query(query);
      const rows = result.toArray();
      
      const createdByMeFilter = {};
      rows.forEach(row => {
        createdByMeFilter[row.id] = true;
      });
      
      store.commit('updateFilter', {
        chartId: 'createdByYouCardFilterContext', 
        ids: createdByMeFilter
      });
    } catch (error) {
      console.error("Error filtering by created by me:", error);
    }
  } else {
    store.commit('updateFilter', {
      chartId: 'createdByYouCardFilterContext', 
      ids: {}
    });
  }
};

const handleChartClick = (chartId, ids) => {
  // This function remains the same as it doesn't directly interact with the data source
  filterContext.value.updateFilter(chartId, ids);
};

const recomputeCounts = debounce(() => {
  computeCardValues();
}, 100);

const isMounted = ref(false);

onMounted(async () => {
  await store.commit('resetFilters');
  isMounted.value = true;
  recomputeCounts();
});

watch(
  () => filterContext.value.filtersLastUpdated,
  () => {
    if (isMounted.value) {
      recomputeCounts();
    }
  },
  { deep: true }
);
</script>

<style scoped>
.v-chip {
  padding: 0 4px !important;
}
.v-data-table .v-icon {
  opacity: 0.6;
}
</style>